import { useI18n, Trans } from '~/common/context';
import { RecordValues } from '@wyz/types';
import { Button, Card, Container, Flex, Text, WizardSteps } from '@wyz/ui';
import * as React from 'react';
import { QuotePricingStep } from '~/purchase/components';
// eslint-disable-next-line @wyz/module/no-relative-import
import ApplicationStep from './application-step';
import { QuotePricingPolicyHelpers } from '~/purchase/helpers';
import { usePolicyFormStepper } from '~/purchase/hooks';
import {
  PRICING_STATUS_ENUM,
  QUOTE_PRICING_POLICY_FORM_STEPS,
} from '~/purchase/schemas';
import {
  IGlobalDefaultQuotePricingFormData,
  IPricingStatus,
  QuotePricingPolicyFormStep,
} from '~/purchase/types';
import { FEATURE_CONTEXT } from '~/common/schemas';
type Step = RecordValues<
  typeof QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX
>;

type Props = {
  onSubmit: (
    formData: IGlobalDefaultQuotePricingFormData,
    status: IPricingStatus,
  ) => void;
  isSubmitting: boolean;
  defaultValues: IGlobalDefaultQuotePricingFormData;
  policyGroupId?: number;
};

function QuoteDefaultPricingPolicyForm({
  onSubmit,
  isSubmitting,
  defaultValues,
  policyGroupId,
}: Props) {
  const { t } = useI18n();
  const [formState, setFormState] =
    React.useState<IGlobalDefaultQuotePricingFormData>(defaultValues);

  const [currentStepIndex, stepperHandlers] = usePolicyFormStepper<Step>({});
  const currentStepKey = QUOTE_PRICING_POLICY_FORM_STEPS[currentStepIndex];

  return (
    <>
      <WizardSteps
        steps={(
          Object.keys(
            QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX,
          ) as Array<QuotePricingPolicyFormStep>
        ).map((stepKey) => ({
          id: stepKey,
          label: t(
            QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_LABELS[stepKey],
          ),
        }))}
        activeStep={currentStepIndex}
        onSelectStep={() => {}} // no switch between step using the wizard, only the button prev/next should be used, otherwise, we won't validate the form correctly
      />
      <Container>
        {currentStepIndex ==
          QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX
            .APPLICATION && (
          <ApplicationStep
            globalFormData={formState}
            policyGroupId={policyGroupId}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({
                ...prev,
                APPLICATION: formData,
              }));
            }}
          />
        )}
        {currentStepIndex ==
          QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX.PRICING && (
          <QuotePricingStep
            globalFormData={formState}
            onSubmit={(formData, status) => {
              setFormState((prev) => {
                const nextState = { ...prev, PRICING: formData };
                onSubmit(nextState, status);
                return nextState;
              });
            }}
            supplierIds={formState.APPLICATION?.customers ?? []}
            featureContext={FEATURE_CONTEXT.QUOTE_PRICING_POLICY_BRANDS}
          />
        )}

        <Card className='sticky' style={{ bottom: '5%' }}>
          <Card.Body>
            <Flex justifyContent={'between'}>
              <Button
                type='button'
                id='previous'
                variant='link'
                disabled={
                  isSubmitting ||
                  currentStepIndex === QuotePricingPolicyHelpers.FIRST_STEP
                }
                onClick={stepperHandlers.onPrevious}
              >
                <Trans i18nKey='app.common.button.previous' />
              </Button>
              <Flex className='space-x-2' alignItems='center'>
                <Text heading='sm' color='secondary'>
                  {formState.PRICING?.name}
                </Text>
                <Text heading='sm' color='secondary-gray'>
                  <Trans
                    i18nKey='app.common.step_value'
                    values={{
                      stepNumber: currentStepIndex + 1,
                      stepName: t(
                        QuotePricingPolicyHelpers
                          .QUOTE_PRICING_POLICY_STEP_LABELS[currentStepKey],
                      ),
                    }}
                  />
                </Text>
                {currentStepIndex === QuotePricingPolicyHelpers.LAST_STEP && (
                  <Button
                    id={'save-as-draft'}
                    data-status={PRICING_STATUS_ENUM.DRAFT}
                    variant='secondary'
                    form={currentStepIndex.toString()}
                    type='submit'
                    isLoading={isSubmitting}
                    children={
                      <Trans i18nKey='app.common.button.save_as_draft' />
                    }
                  />
                )}
                <Button
                  id='next'
                  data-status={PRICING_STATUS_ENUM.ENABLED}
                  variant='primary'
                  type='submit'
                  form={currentStepIndex.toString()}
                  isLoading={isSubmitting}
                  children={
                    currentStepIndex < QuotePricingPolicyHelpers.LAST_STEP
                      ? t('app.common.buttons.continue')
                      : t('app.common.button.save')
                  }
                />
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
export default QuoteDefaultPricingPolicyForm;
